import { type PointerEvent, useRef } from "react";
import { ToastStack, type ToastStackProps } from "../components/arc/toast-stack/toast-stack";
import styles from "./toasts.module.css";

/** Moved further than this between press and release: a swipe, not a click. */
const SLOP = 6;

export interface ClickableToastStackProps extends ToastStackProps {
  /** A toast was clicked (not its buttons, not swiped): its text, title and description. */
  onToastClick: (text: string) => void;
}

/**
 * Arc's toast stack, where clicking a toast does something. Arc's toast has no click of its own
 * and its files aren't edited, so the click is read here, around it. A keyboard reaches the toast's
 * buttons only: whatever a click opens must also be reachable another way (the bell).
 */
export function ClickableToastStack({ onToastClick, ...props }: ClickableToastStackProps) {
  const pressed = useRef<{ x: number; y: number } | null>(null);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: a shortcut for the pointer; the bell has every notification
    // biome-ignore lint/a11y/useKeyWithClickEvents: as above
    <div
      className={styles.clickable}
      onPointerDown={(event: PointerEvent) => {
        pressed.current = { x: event.clientX, y: event.clientY };
      }}
      onClick={(event) => {
        const from = pressed.current;
        pressed.current = null;
        const target = event.target as Element;
        const item = target.closest("li");
        if (!item || target.closest("button, a")) return;
        if (from && Math.hypot(event.clientX - from.x, event.clientY - from.y) > SLOP) return;
        onToastClick(item.textContent ?? "");
      }}
    >
      <ToastStack {...props} />
    </div>
  );
}
